<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { getCurrentInstance, computed, ref, watch, watchEffect, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from "@kyvg/vue3-notification"
import TranscoderService from '@/services/TranscoderService'
import FormButton from '../../components/Atoms/FormButton.vue'
import IconBase from '../../components/icon/IconBase.vue'
import Tooltip from '../Atoms/Tooltip.vue'
import IconInfoSmall from '../icon/IconInfoSmall.vue'
import IconChevronUpDown from '../icon/IconChevronUpDown.vue'
import FormSwitch from '../Atoms/FormSwitch.vue'
import DropDownMenu from '../Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue'
import FormInput from '../Atoms/FormInput.vue'
import FormUpload from '../Atoms/FormUpload.vue'
import { isNumeric } from '../../utils'
import AlertModal from '../modals/AlertModal.vue'
import ConfirmModal from '../modals/ConfirmModal.vue'
import { track, EVENTS } from '@/lib/tracking'

const instance = getCurrentInstance()
const root = instance.proxy.$root
const store = useStore()
const { notify } = useNotification()

const props = defineProps({
  transcoder: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id')
    }
  },
  streamAlive: {
    type: Boolean,
    default: false
  },
})

// Refs
const formErrors = ref({
  'videoBitrate': { error: null, show: false },
  'width': { error: null, show: false },
  'height': { error: null, show: false },
  'selectedCodec': { error: null, show: false },
  'selectedFps': { error: null, show: false },
  'audioBitrate': { error: null, show: false },
  'volume': { error: null, show: false },
  'selectedAudioTrack': { error: null, show: false }
})

const features = ref({
  embedWatermark: {
    error: false,
    enabled: false,
    value: null,
    valueType: 'string'
  },
  embedWatermarkPosition: {
    error: false,
    enabled: true,
    value: 'tr',
    title: 'Top Right',
    valueType: 'string'
  },
})

const featureProcessing = ref({
  embedWatermark: false,
  embedWatermarkPosition: false,
})

const dataUpdated = ref(false)

const selectedCodec = ref('H264')
const selectedResolution = ref('1920_1080')
const embedWatermarkTemp = ref(null)
const pendingWatermarkFile = ref(null)
const saveProcessing = ref(null)
const maxPosterUploadMB = ref(2)
const bypassFormWatchEvent = ref(false)
const saveDisabled = ref(true)
const videoBitrate = ref(null)
const audioBitrate = ref(null)
const audioTrack = ref(1)
const volume = ref(1)
const selectedFps = ref(null)
const width = ref(0)
const height = ref(0)

const codecs = ref([
  {
    name: 'H264'
  },
  // {
  // 	name: 'H265/HEVC'
  // }
])

const resolutionOpts = ref([
  { text: "1920x1080", value: "1920_1080" },
  { text: "1280x720", value: "1280_720" },
  { text: "Custom", value: "custom" },
])

const selectedAudioTrack = ref(1)
const audioTracks = ref([
  { name: 1 },
  { name: 2 },
  { name: 3 },
  { name: 4 },
  { name: 5 },
  { name: 6 },
  { name: 7 },
  { name: 8 },
  { name: 9 },
  { name: 10 },
])

const fpsOpts = ref([{ name: 24 }, { name: 25 }, { name: 29.97 }, { name: 30 }, { name: 50 }, { name: 59.94 }, { name: 60 }])

const deinterlace = ref(false)
const interlace = ref(false)
const audioTrackEnabled = ref(false)

const copyVideoFromInput = ref(true)
const copyAudioFromInput = ref(true)
const copyVideoFromInputUi = ref(true)
const copyAudioFromInputUi = ref(true)
const copyVideoSwitchKey = ref(0)
const copyAudioSwitchKey = ref(0)
const pendingCopyToggleType = ref(null)
const fpsDropdownRef = ref(null)
const audioTrackDropdownRef = ref(null)

const getDefaultFormErrors = () => ({
  'videoBitrate': { error: null, show: false },
  'width': { error: null, show: false },
  'height': { error: null, show: false },
  'selectedCodec': { error: null, show: false },
  'selectedFps': { error: null, show: false },
  'audioBitrate': { error: null, show: false },
  'volume': { error: null, show: false },
  'selectedAudioTrack': { error: null, show: false }
})

// Computed
const hasTranscoderWatermarkFeature = computed(() => store.getters['User/hasTranscoderWatermarkFeature'])
const transcoderFlag = computed(() => store.getters['User/transcoderFlag'])

const canSaveWatermarkPosition = computed(() =>
  props.transcoder?.metadata?.embedWatermark &&
  (features.value.embedWatermarkPosition.value !== props.transcoder?.metadata?.embedWatermarkPosition))

const hasExistingVideoCustomConfig = computed(() => {
  const meta = props.transcoder?.metadata
  if (!meta || meta.copyVideo !== false) return false

  return (
    meta.codec != null ||
    meta.resolution != null ||
    meta.videoBitrate != null ||
    meta.fps != null ||
    meta.width != null ||
    meta.height != null ||
    meta.interlace === true ||
    meta.deinterlace === true
  )
})

const hasExistingAudioCustomConfig = computed(() => {
  const meta = props.transcoder?.metadata
  if (!meta || meta.copyAudio !== false) return false

  return (
    meta.audioBitrate != null ||
    meta.volume != null ||
    meta.audioTrack != null ||
    meta.audioTrackEnabled === true
  )
})

const numEq = (a, b) => Number(a) === Number(b)

const hasUnsavedChanges = computed(() => {
  const meta = props.transcoder?.metadata
  if (!meta) return false

  const customVideoSettings = !copyVideoFromInput.value
  const customAudioSettings = !copyAudioFromInput.value

  const videoChanged =
    copyVideoFromInput.value !== meta.copyVideo ||
    interlace.value !== meta.interlace ||
    deinterlace.value !== meta.deinterlace ||
    (
      customVideoSettings && (
        selectedCodec.value !== meta.codec ||
        selectedResolution.value !== meta.resolution ||
        !numEq(videoBitrate.value, meta.videoBitrate) ||
        !numEq(selectedFps.value, meta.fps) ||
        !numEq(width.value, meta.width) ||
        !numEq(height.value, meta.height)
      )
    )

  const audioChanged =
    copyAudioFromInput.value !== meta.copyAudio ||
    (
      customAudioSettings && (
        !numEq(audioBitrate.value, meta.audioBitrate) ||
        !numEq(volume.value, meta.volume) ||
        !numEq(selectedAudioTrack.value, meta.audioTrack) ||
        audioTrackEnabled.value !== meta.audioTrackEnabled
      )
    )

  return videoChanged || audioChanged
})

const isCancelDisabled = computed(() => !hasUnsavedChanges.value)

const isDisabled = computed(() => {
  const meta = props.transcoder?.metadata
  if (!meta) return true

  const hasError = Object.values(formErrors.value).some(item => Boolean(item.error))
  if (hasError) return true

  const customVideoSettings = !copyVideoFromInput.value
  const customAudioSettings = !copyAudioFromInput.value

  // required fields for enabled custom sections
  const videoMissingRequired =
    customVideoSettings &&
    (
      !selectedCodec.value ||
      !selectedFps.value ||
      !videoBitrate.value ||
      (selectedResolution.value === 'custom' && (!width.value || !height.value))
    )

  const audioMissingRequired =
    customAudioSettings && !audioBitrate.value

  if (videoMissingRequired || audioMissingRequired) return true
  return !hasUnsavedChanges.value
})

const watermarkUrl = computed(() => {
  if (embedWatermarkTemp.value) return embedWatermarkTemp.value
  const imageId = props.transcoder?.metadata?.embedWatermark
  return imageId ? `https://castr-transcoder.s3.amazonaws.com/embedImages/${imageId}` : null
})

// Watchers
watch(interlace, () => {
  if (interlace.value === true) deinterlace.value = false
})
watch(deinterlace, () => {
  if (deinterlace.value === true) interlace.value = false
})

// Scan mode is a single either/or choice surfaced as a segmented control,
// backed by the mutually-exclusive interlace/deinterlace flags.
const scanMode = computed(() => deinterlace.value ? 'deinterlace' : (interlace.value ? 'interlace' : 'none'))
const setScanMode = (mode) => {
  deinterlace.value = mode === 'deinterlace'
  interlace.value = mode === 'interlace'
}

// onMounted(async () => {
//     if (props.transcoder?.metadata) {
//     interlace.value = props.transcoder.metadata.interlace
//     deinterlace.value = props.transcoder.metadata.deinterlace
//     features.value.embedWatermarkPosition.value = props.transcoder.metadata?.embedWatermarkPosition
//     features.value.embedWatermark.value = props.transcoder.metadata?.embedWatermark
//     if (features.value.embedWatermarkPosition.value) {
//       const posTitles = { 'tr': 'Top Right', 'tl': 'Top Left', 'cc': 'Center', 'bl': 'Bottom Left', 'br': 'Bottom Right' }
//       features.value.embedWatermarkPosition.title = posTitles[features.value.embedWatermarkPosition.value] || 'Top Right'
//     }
//     if (props.transcoder.metadata.copyVideo === false) {
//       copyVideoFromInput.value = false
//       selectedFps.value = props.transcoder.metadata.fps
//       selectedCodec.value = props.transcoder.metadata.codec
//       selectedResolution.value = props.transcoder.metadata.resolution
//       videoBitrate.value = props.transcoder.metadata.videoBitrate
//       width.value = props.transcoder.metadata.width
//       height.value = props.transcoder.metadata.height
//     }
//     if (props.transcoder.metadata.copyAudio === false) {
//       copyAudioFromInput.value = false
//       audioBitrate.value = props.transcoder.metadata.audioBitrate
//       volume.value = props.transcoder.metadata.volume
//       audioTrack.value = props.transcoder.metadata.audioTrack
//       audioTrackEnabled.value = props.transcoder.metadata.audioTrackEnabled
//     }
//     dataUpdated.value = true
//   }

// })

// Update values if transcoder updated
const applyMetadataToForm = (metadata) => {
  if (!metadata) return

  const copyVideoEnabled = metadata.copyVideo !== false
  const copyAudioEnabled = metadata.copyAudio !== false

  copyVideoFromInput.value = copyVideoEnabled
  copyAudioFromInput.value = copyAudioEnabled
  copyVideoFromInputUi.value = copyVideoEnabled
  copyAudioFromInputUi.value = copyAudioEnabled

  selectedCodec.value = metadata.codec || 'H264'
  selectedResolution.value = metadata.resolution || '1920_1080'
  selectedFps.value = metadata.fps ?? null
  videoBitrate.value = metadata.videoBitrate ?? null
  width.value = metadata.width ?? 0
  height.value = metadata.height ?? 0

  audioBitrate.value = metadata.audioBitrate ?? null
  volume.value = metadata.volume ?? 1
  const track = metadata.audioTrack ?? 1
  audioTrack.value = track
  selectedAudioTrack.value = track
  audioTrackEnabled.value = Boolean(metadata.audioTrackEnabled)

  interlace.value = Boolean(metadata.interlace)
  deinterlace.value = Boolean(metadata.deinterlace)

  features.value.embedWatermarkPosition.value = metadata.embedWatermarkPosition || 'tr'
  features.value.embedWatermark.value = metadata.embedWatermark
  const posTitles = { 'tr': 'Top Right', 'tl': 'Top Left', 'cc': 'Center', 'bl': 'Bottom Left', 'br': 'Bottom Right' }
  features.value.embedWatermarkPosition.title = posTitles[features.value.embedWatermarkPosition.value] || 'Top Right'
  embedWatermarkTemp.value = null

  formErrors.value = getDefaultFormErrors()
  pendingCopyToggleType.value = null
}

watch(() => props.transcoder?.metadata, () => {
  if (props.transcoder?.metadata) {
    applyMetadataToForm(props.transcoder.metadata)
    dataUpdated.value = true
  }
}, {
  immediate: true
})

// Methods

const onFpsSelect = (fps) => {
  selectedFps.value = fps.name
  fpsDropdownRef.value?.hideDropDownMenu()
}
const onAudioTrackSelect = (track) => {
  selectedAudioTrack.value = track.name
  audioTrack.value = track.name
  audioTrackDropdownRef.value?.hideDropDownMenu()
}
const onEmbedPosterPreview = (file) => {
  const imageFile = file

  if (!imageFile) return

  if (imageFile.type.indexOf('image/') === -1) {
    showModal('embed-upload-format')
    return
  }

  let maxSize = 1
  maxPosterUploadMB.value = maxSize

  maxSize *= 1024 * 1024

  if (imageFile.size > maxSize) {
    showModal('embed-image-size-exceed')
    return
  }

  const validImageTypes = ['png', 'jpeg', 'jpg']
  const uploadedImageFileType = imageFile.type.replace('image/', '')

  if (
    validImageTypes &&
    validImageTypes.indexOf(uploadedImageFileType) === -1
  ) {
    showModal('embed-image-mime-type')
    return
  }

  pendingWatermarkFile.value = imageFile
  imageReader(imageFile, base64 => {
    embedWatermarkTemp.value = base64
  })
}

const onWatermarkFileInputChange = (event) => {
  const file = event.target.files?.[0]
  if (file) onEmbedPosterPreview(file)
}

function imageReader(file, cb) {
  if (!file) {
    return
  }
  var reader = new FileReader()
  reader.onload = function () {
    if (cb) {
      cb(reader.result)
    }
  }
  reader.readAsDataURL(file)
}

const saveWatermarkPosition = async () => {
  const newPosition = features.value.embedWatermarkPosition.value
  await saveWatermarkSettings('embedWatermarkPosition', newPosition, true)
}
const hitUpload = (type) => {
  const selector = type === 'watermark' ? '#embed-watermark-input' : '#embed-poster-input'
  document.querySelector(selector)?.click()
}

const formFieldValidateValue = (metaField, formValue) => {

  if (bypassFormWatchEvent.value) {
    return
  }

  let metaValue = props.transcoder.metadata[metaField]
  if (formValue === metaValue) {
    saveDisabled.value = true
    return
  }

  const validationLambda = () => {
    if (!copyVideoFromInput.value) {
      if (metaField === 'videoBitrate') {
        if (Number(videoBitrate.value) > 15000 || Number(videoBitrate.value) < 250) {
          formErrors.value.videoBitrate = { error: 'Bitrate value should be from 250 to 15000', show: true }
          return
        } else {
          formErrors.value.videoBitrate = { error: null, show: false }
        }
      }

      if (selectedResolution.value === 'custom') {
        if (metaField === 'width') {
          if (!width.value) {
            formErrors.value.width = { error: 'required', show: true }
            return
          } else {
            formErrors.value.width = { error: null, show: false }
          }

          if (Number(width.value) > 3840 || Number(width.value) < 320) {
            formErrors.value.width = { error: 'Allowed range from 320 to 3840', show: true }
            return
          } else {
            formErrors.value.width = { error: null, show: false }
          }
        }

        if (metaField === 'height') {
          if (!height.value) {
            formErrors.value.height = { error: 'required', show: true }
            return
          } else {
            formErrors.value.height = { error: null, show: false }
          }

          if (Number(height.value) > 2160 || Number(height.value) < 240) {
            formErrors.value.height = { error: 'Allowed range from 240 to 2160', show: true }
            return
          } else {
            formErrors.value.height = { error: null, show: false }
          }
        }
      } else {
        formErrors.value.width = { error: null, show: false }
        formErrors.value.height = { error: null, show: false }
      }
    } else {
      formErrors.value.videoBitrate = { error: null, show: true }
      formErrors.value.width = { error: null, show: true }
      formErrors.value.height = { error: null, show: true }
      formErrors.value.selectedCodec = { error: null, show: true }
      formErrors.value.selectedFps = { error: null, show: true }
    }

    if (!copyAudioFromInput.value) {
      if (audioBitrate.value && (metaField === 'audioBitrate')) {
        if (!isNumeric(audioBitrate.value)) {
          formErrors.value.audioBitrate = { error: 'Only numeric value allowed', show: true }
          return
        } else {
          formErrors.value.audioBitrate = { error: null, show: false }
        }

        if (Number(audioBitrate.value) > 320 || Number(audioBitrate.value) < 32) {
          formErrors.value.audioBitrate = { error: 'Allowed range from 32 to 320', show: true }
          return
        } else {
          formErrors.value.audioBitrate = { error: null, show: false }
        }
      }

      if (volume.value && (metaField === 'volume')) {
        if (!isNumeric(volume.value)) {
          formErrors.value.volume = { error: 'Only numeric value allowed', show: true }
          return
        } else {
          formErrors.value.volume = { error: null, show: false }
        }

        if (Number(volume.value) > 2 || Number(volume.value) < 0) {
          formErrors.value.volume = { error: 'Allowed range from 0 to 2', show: true }
          return
        } else {
          formErrors.value.volume = { error: null, show: false }
        }
      }

    } else {
      formErrors.value.audioBitrate = { error: null, show: true }
      formErrors.value.volume = { error: null, show: true }
      formErrors.value.selectedAudioTrack = { error: null, show: true }
    }
  }

  validationLambda()
}

const onCopyVideoToggleUpdate = (nextValue) => {
  copyVideoFromInputUi.value = nextValue

  if (
    copyVideoFromInput.value === false &&
    nextValue === true &&
    hasExistingVideoCustomConfig.value
  ) {
    pendingCopyToggleType.value = 'video'
    showModal('confirm-copy-from-input-change')
    return
  }

  copyVideoFromInput.value = nextValue
}

const onCopyAudioToggleUpdate = (nextValue) => {
  copyAudioFromInputUi.value = nextValue

  if (
    copyAudioFromInput.value === false &&
    nextValue === true &&
    hasExistingAudioCustomConfig.value
  ) {
    pendingCopyToggleType.value = 'audio'
    showModal('confirm-copy-from-input-change')
    return
  }

  copyAudioFromInput.value = nextValue
}

const onConfirmCopyFromInputChange = () => {
  if (pendingCopyToggleType.value === 'video') {
    copyVideoFromInput.value = true
    copyVideoFromInputUi.value = true
  }
  if (pendingCopyToggleType.value === 'audio') {
    copyAudioFromInput.value = true
    copyAudioFromInputUi.value = true
  }
  pendingCopyToggleType.value = null
}

const onCancelCopyFromInputChange = () => {
  if (pendingCopyToggleType.value === 'video') {
    copyVideoFromInput.value = false
    copyVideoFromInputUi.value = false
    copyVideoSwitchKey.value += 1
  }
  if (pendingCopyToggleType.value === 'audio') {
    copyAudioFromInput.value = false
    copyAudioFromInputUi.value = false
    copyAudioSwitchKey.value += 1
  }
  pendingCopyToggleType.value = null
}

const cancelSettings = () => {
  applyMetadataToForm(props.transcoder?.metadata)
  copyVideoSwitchKey.value += 1
  copyAudioSwitchKey.value += 1
}

const saveSettings = async () => {
  saveProcessing.value = true

  const data = {
    copyVideo: copyVideoFromInput.value,
    copyAudio: copyAudioFromInput.value,
    codec: selectedCodec.value,
    fps: selectedFps.value,
    resolution: selectedResolution.value,
    width: width.value,
    height: height.value,
    videoBitrate: videoBitrate.value,
    audioBitrate: audioBitrate.value,
    volume: volume.value,
    deinterlace: deinterlace.value,
    interlace: interlace.value,
    audioTrack: selectedAudioTrack.value,
    audioTrackEnabled: audioTrackEnabled.value,
  }
  try {
    await TranscoderService.setTranscoderSettings(
      props.transcoder._id,
      data
    )

    await store.dispatch('Transcoders/getTranscoders')
    await store.dispatch('Transcoders/getTranscoder', props.transcoder._id)

    track(EVENTS.TRANSCODER_PROFILE_CONFIGURED, {
      codec: selectedCodec.value,
      resolution: selectedResolution.value,
      fps: selectedFps.value,
      videoBitrate: videoBitrate.value,
    })

    notify({
      group: 'success',
      text: 'Transcoder settings saved',
    })
  } catch (err) {
    console.log(err)
    notify({
      group: 'error',
      title: 'Set transcoder settings failed',
      text: err.message || 'update settings failed'
    })
  }
  saveProcessing.value = false
}


const saveEmbedPoster = async (type) => {
  const featureProp = type === 'watermark' ? 'embedWatermark' : 'embedPoster'
  features.value[featureProp].error = false

  const selector = type === 'watermark' ? '#embed-watermark-input' : '#embed-poster-input'
  const imageInput = document.querySelector(selector)
  const imageFile = type === 'watermark'
    ? pendingWatermarkFile.value
    : imageInput?.files?.[0]

  if (!imageFile) {
    features.value[featureProp].error = 'please pick an image file'
    return
  }

  featureProcessing.value[featureProp] = true

  // -- upload reuqest --
  const fdata = new FormData()
  fdata.append('file', imageFile)
  const res = await TranscoderService.uploadTranscoderLogo(
    props.transcoder._id,
    fdata,
  )

  if (res.success) {
    if (type === 'watermark') {
      embedWatermarkTemp.value = null
      pendingWatermarkFile.value = null
      if (imageInput) imageInput.value = ''

      await store.dispatch('Transcoders/getTranscoders')
      await store.dispatch('Transcoders/getTranscoder', props.transcoder._id)
    }

    features.value[featureProp].value = res.uploadId

    notify({
      group: 'success',
      text: `${type === 'watermark' ? 'Watermark' : 'Poster'} uploaded successfully.`
    })
  } else {
    features.value[featureProp].error = 'Unable to upload the image. Please try again later.'
  }

  featureProcessing.value[featureProp] = false
}

const cancelUpload = (type) => {
  const selector = type === 'watermark' ? '#embed-watermark-input' : '#embed-poster-input'
  const previewFlag = type === 'watermark' ? embedWatermarkTemp.value : embedPosterTemp.value
  if (previewFlag) {
    if (type === 'watermark') {
      embedWatermarkTemp.value = null
      pendingWatermarkFile.value = null
    }

    document.querySelector(selector).value = null
    return
  }

  removePoster(type)
}

const removePoster = async (type) => {
  try {
    const settingsProp =
      type === 'watermark' ? 'embedWatermark' : 'embedPoster'
    await saveWatermarkSettings(settingsProp, null, true)
    embedWatermarkTemp.value = null
    pendingWatermarkFile.value = null
    features.value[settingsProp].value = null
    await store.dispatch('Transcoders/getTranscoders')
    await store.dispatch("Transcoders/getTranscoder", props.transcoder._id)

  } catch (e) {
    console.log(e)
  }
}

const saveWatermarkSettings = async (key, value, successToast = false) => {
  featureProcessing.value[key] = true
  try {
    await TranscoderService.setTranscoderWatermarkSettings(
      props.transcoder._id,
      { key, value }
    )

    if (successToast) {
      notify({
        group: 'success',
        text: 'Transcoder watermark updated',
      })
    }
    await store.dispatch('Transcoders/getTranscoder', props.transcoder._id)
  } catch (err) {
    console.log(err)
    notify({
      group: 'error',
      title: 'Set transcoder watermark settings failed',
      text: err.message || 'update settings failed'
    })
  }
  featureProcessing.value[key] = false
}

const changeWatermarkPosition = (position, title) => {
  features.value.embedWatermarkPosition.value = position
  features.value.embedWatermarkPosition.title = title
}

</script>
<template>
  <section class="bg-surface-2 rounded-xl p-4 md:p-6 max-w-[100vw]">
    <!-- VIDEO -->
    <div class="bg-surface-1 rounded-xl p-4 md:p-5">
      <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div class="min-w-0">
          <h3 class="flex gap-1.5 items-center text-base font-semibold text-white mb-0.5">
            Video
            <Tooltip>
              <IconBase class="text-surface-7">
                <IconInfoSmall />
              </IconBase>
              <template #content>
                <h5 class="font-medium">Codecs</h5>
                <p class="text-surface-8 text-xs !mb-3">H264</p>
                <h5 class="font-medium">Resolution</h5>
                <p class="text-surface-8 text-xs">Max Width: 1920</p>
                <p class="text-surface-8 text-xs">Max Height: 1080</p>
                <p class="text-surface-8 text-xs">Min Width: 320</p>
                <p class="text-surface-8 text-xs !mb-3">Min Height: 240</p>
                <h5 class="font-medium">Video Bitrate</h5>
                <p class="text-surface-8 text-xs">Max Value: 15000</p>
                <p class="text-surface-8 text-xs">Min Value: 250</p>
              </template>
            </Tooltip>
          </h3>
          <p class="text-tiny text-surface-7 mb-0">{{ copyVideoFromInput ? (streamAlive ? 'Output matches the source. Customizable while the stream is offline.' : 'Output matches the source. Turn off to customize.') : 'Customize the output video below.' }}</p>
        </div>
        <FormSwitch :key="copyVideoSwitchKey" :disabled="streamAlive" :modelValue="copyVideoFromInputUi"
          @update:modelValue="onCopyVideoToggleUpdate" size="sm" class="text-surface-8 shrink-0" label="Copy from input" />
      </div>

      <div v-if="!copyVideoFromInput" class="mt-4 flex flex-col gap-4">
        <!-- Codec (read-only) -->
        <div>
          <label class="block mb-1 text-sm text-surface-8">Codec</label>
          <div class="h-8 px-3 flex items-center rounded-md bg-surface-2 text-tiny text-surface-9">H264</div>
        </div>
        <!-- Resolution -->
        <div>
          <label class="block mb-1 text-sm text-surface-8">Resolution</label>
          <div class="inline-flex items-center gap-1 bg-surface-2 rounded-lg p-1 max-w-full overflow-x-auto">
            <FormButton v-for="option in resolutionOpts" :key="option.value"
              :custom-classes="'px-3 py-1.5 rounded-md text-sm font-medium transition-all text-nowrap ' + (selectedResolution === option.value ? '!bg-surface-4 !text-white shadow-sm' : 'text-surface-8 hover:text-white hover:bg-surface-3')"
              @click="selectedResolution = option.value">{{ option.text }}</FormButton>
          </div>
        </div>
        <!-- Custom width/height -->
        <div v-if="selectedResolution === 'custom'" class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
          <div class="min-w-0">
            <FormInput type="number" v-model="width" label="Width" :is-error="formErrors.width.show"
              @input="formFieldValidateValue('width', width)" />
            <p v-if="formErrors.width.error" class="mt-1 mb-0 text-tiny text-error leading-snug">{{ formErrors.width.error }}</p>
          </div>
          <div class="min-w-0">
            <FormInput type="number" v-model="height" label="Height" :is-error="formErrors.height.show"
              @input="formFieldValidateValue('height', height)" />
            <p v-if="formErrors.height.error" class="mt-1 mb-0 text-tiny text-error leading-snug">{{ formErrors.height.error }}</p>
          </div>
        </div>
        <!-- Bitrate + FPS -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
          <div class="min-w-0">
            <FormInput type="number" label="Video Bitrate" v-model="videoBitrate" :is-error="formErrors.videoBitrate.show"
              @input="formFieldValidateValue('videoBitrate', videoBitrate)" />
            <p v-if="formErrors.videoBitrate.error" class="mt-1 mb-0 text-tiny text-error leading-snug">{{ formErrors.videoBitrate.error }}</p>
          </div>
          <div>
            <label class="block mb-1 text-sm text-surface-8">FPS</label>
            <DropDownMenu ref="fpsDropdownRef" full-width type="surface-4">
              <template #toggle-button>
                {{ selectedFps ? selectedFps : 'Select an option' }}
                <icon-base class="ml-auto">
                  <icon-chevron-up-down />
                </icon-base>
              </template>
              <template #menu-items>
                <DropDownMenuItem v-for="(fps, idx) in fpsOpts" :key="idx" @click="onFpsSelect(fps)">
                  {{ fps.name }}
                </DropDownMenuItem>
              </template>
            </DropDownMenu>
          </div>
        </div>
        <!-- Scan mode (single either/or control) -->
        <div>
          <div class="flex items-center gap-1.5 mb-1">
            <label class="text-sm text-surface-8 mb-0">Scan mode</label>
            <Tooltip content="Deinterlace converts interlaced video to progressive; Interlace does the reverse. Leave both off to keep the source as-is.">
              <IconBase class="text-surface-7">
                <IconInfoSmall />
              </IconBase>
            </Tooltip>
          </div>
          <div class="inline-flex items-center gap-1 bg-surface-2 rounded-lg p-1 max-w-full overflow-x-auto">
            <FormButton
              :custom-classes="'px-3 py-1.5 rounded-md text-sm font-medium transition-all text-nowrap ' + (scanMode === 'deinterlace' ? '!bg-surface-4 !text-white shadow-sm' : 'text-surface-8 hover:text-white hover:bg-surface-3')"
              @click="setScanMode(scanMode === 'deinterlace' ? 'none' : 'deinterlace')">Deinterlace</FormButton>
            <FormButton
              :custom-classes="'px-3 py-1.5 rounded-md text-sm font-medium transition-all text-nowrap ' + (scanMode === 'interlace' ? '!bg-surface-4 !text-white shadow-sm' : 'text-surface-8 hover:text-white hover:bg-surface-3')"
              @click="setScanMode(scanMode === 'interlace' ? 'none' : 'interlace')">Interlace</FormButton>
          </div>
        </div>
      </div>
    </div>

    <!-- AUDIO -->
    <div class="bg-surface-1 rounded-xl p-4 md:p-5 mt-4">
      <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div class="min-w-0">
          <h3 class="flex gap-1.5 items-center text-base font-semibold text-white mb-0.5">
            Audio
            <Tooltip>
              <IconBase class="text-surface-7">
                <IconInfoSmall />
              </IconBase>
              <template #content>
                <h5 class="font-medium">Audio Bitrate</h5>
                <p class="text-surface-8 text-xs">Min value: 32</p>
                <p class="text-surface-8 text-xs !mb-3">Max value: 320</p>
                <h5 class="font-medium">Volume</h5>
                <p class="text-surface-8 text-xs">Min value: 0</p>
                <p class="text-surface-8 text-xs">Max value: 2</p>
              </template>
            </Tooltip>
          </h3>
          <p class="text-tiny text-surface-7 mb-0">{{ copyAudioFromInput ? (streamAlive ? 'Output matches the source. Customizable while the stream is offline.' : 'Output matches the source. Turn off to customize.') : 'Customize the output audio below.' }}</p>
        </div>
        <FormSwitch :key="copyAudioSwitchKey" :disabled="streamAlive" :modelValue="copyAudioFromInputUi"
          @update:modelValue="onCopyAudioToggleUpdate" size="sm" class="text-surface-8 shrink-0" label="Copy from input" />
      </div>

      <div v-if="!copyAudioFromInput" class="mt-4 flex flex-col gap-4">
        <div>
          <div class="flex items-center gap-2">
            <FormSwitch v-model="audioTrackEnabled" size="sm" switch label="Filter track" />
            <Tooltip content="Choose a specific audio track to transcode">
              <IconBase class="text-surface-7">
                <IconInfoSmall />
              </IconBase>
            </Tooltip>
          </div>
          <div v-show="audioTrackEnabled" class="mt-2">
            <label class="block mb-1 text-sm text-surface-8">Audio track</label>
            <DropDownMenu ref="audioTrackDropdownRef" full-width type="surface-4">
              <template #toggle-button>
                {{ selectedAudioTrack }}
                <icon-base class="ml-auto">
                  <icon-chevron-up-down />
                </icon-base>
              </template>
              <template #menu-items>
                <DropDownMenuItem v-for="(track, idx) in audioTracks" :key="idx" @click="onAudioTrackSelect(track)">
                  {{ track.name }}
                </DropDownMenuItem>
              </template>
            </DropDownMenu>
          </div>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
          <div class="min-w-0">
            <FormInput type="number" label="Audio Bitrate" v-model="audioBitrate"
              @input="formFieldValidateValue('audioBitrate', audioBitrate)" :is-error="formErrors.audioBitrate.show" />
            <p v-if="formErrors.audioBitrate.error" class="mt-1 mb-0 text-tiny text-error leading-snug">{{ formErrors.audioBitrate.error }}</p>
          </div>
          <div class="min-w-0">
            <FormInput type="number" label="Volume" v-model="volume" @input="formFieldValidateValue('volume', volume)"
              :is-error="formErrors.volume.show" />
            <p v-if="formErrors.volume.error" class="mt-1 mb-0 text-tiny text-error leading-snug">{{ formErrors.volume.error }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- WATERMARK -->
    <div v-if="!copyVideoFromInput" class="bg-surface-1 rounded-xl p-4 md:p-5 mt-4">
      <h3 class="text-base font-semibold text-white mb-0.5">Watermarking</h3>
      <p class="text-tiny text-surface-7 mb-3">Overlay a logo on the transcoded output.</p>

      <Tooltip :active="!hasTranscoderWatermarkFeature" content="Upgrade to access this feature">
        <div class="flex flex-col gap-3">
          <!-- Keep in DOM while preview is shown so Save / Change still work -->
          <input
            id="embed-watermark-input"
            type="file"
            class="hidden"
            accept="image/png,image/jpeg,image/jpg"
            @change="onWatermarkFileInputChange"
          />
          <!-- Preview / dropzone -->
          <div v-if="watermarkUrl || embedWatermarkTemp">
            <img :src="watermarkUrl ? watermarkUrl : embedWatermarkTemp"
              class="w-40 rounded-lg ring-1 ring-white/10 bg-surface-darker object-contain">
          </div>
          <FormUpload v-else type="secondary" v-show="hasTranscoderWatermarkFeature" name="watermark"
            id="embed-watermark-input-picker" label="No file chosen" :onFileChange="onEmbedPosterPreview"
            drop-placeholder="Drop file here..." />

          <!-- Position -->
          <div>
            <label class="block mb-1 text-sm text-surface-8">Position</label>
            <DropDownMenu :disabled="!hasTranscoderWatermarkFeature" full-width type="surface-4" class="max-w-[220px]">
              <template #toggle-button>
                {{ features.embedWatermarkPosition.title }}
                <icon-base class="ml-auto">
                  <icon-chevron-up-down />
                </icon-base>
              </template>
              <template #menu-items>
                <DropDownMenuItem @click="changeWatermarkPosition('tr', 'Top Right')">Top Right</DropDownMenuItem>
                <DropDownMenuItem @click="changeWatermarkPosition('tl', 'Top Left')">Top Left</DropDownMenuItem>
                <DropDownMenuItem @click="changeWatermarkPosition('cc', 'Center')">Center</DropDownMenuItem>
                <DropDownMenuItem @click="changeWatermarkPosition('br', 'Bottom Right')">Bottom Right</DropDownMenuItem>
                <DropDownMenuItem @click="changeWatermarkPosition('bl', 'Bottom Left')">Bottom Left</DropDownMenuItem>
              </template>
            </DropDownMenu>
          </div>

          <!-- Actions -->
          <div class="flex flex-wrap items-center gap-2">
            <FormButton v-if="embedWatermarkTemp" :disabled="featureProcessing.embedWatermark" type="main" size="sm"
              @click="saveEmbedPoster('watermark')" :label="featureProcessing.embedWatermark ? 'Saving…' : 'Save'" />
            <FormButton v-show="canSaveWatermarkPosition && !embedWatermarkTemp"
              :disabled="featureProcessing.embedWatermarkPosition" size="sm" type="main" @click="saveWatermarkPosition"
              :label="featureProcessing.embedWatermarkPosition ? 'Saving…' : 'Save position'" />
            <FormButton v-if="!embedWatermarkTemp"
              :disabled="featureProcessing.embedWatermark || !hasTranscoderWatermarkFeature" type="secondary" size="sm"
              @click="hitUpload('watermark')" :label="watermarkUrl ? 'Change' : 'Select watermark'" />
            <FormButton v-if="embedWatermarkTemp || watermarkUrl"
              :disabled="featureProcessing.embedWatermark || !hasTranscoderWatermarkFeature" type="secondary" size="sm"
              @click="cancelUpload('watermark')">
              <span v-if="embedWatermarkTemp && watermarkUrl">Remove</span>
              <span v-else-if="embedWatermarkTemp">Cancel</span>
              <span v-else>Remove</span>
            </FormButton>
          </div>
          <p v-show="features.embedWatermark.error" class="text-tiny text-error mb-0">
            {{ features.embedWatermark.error }}
          </p>
        </div>
      </Tooltip>
    </div>

    <!-- ACTIONS -->
    <div class="flex flex-col-reverse sm:flex-row gap-2 mt-5">
      <Tooltip :active="!transcoderFlag || streamAlive">
        <FormButton class="w-full justify-center sm:w-auto" :disabled="isDisabled || !transcoderFlag || streamAlive || saveProcessing" @click="saveSettings">
          {{ saveProcessing ? 'Saving…' : 'Save changes' }}
        </FormButton>
        <template #content>
          <span v-if="streamAlive">Settings can be changed while the stream is offline.</span>
          <span v-else><a class="underline" target="_blank" href="https://castr.com/app/subscribe">Upgrade</a> plan to save changes.</span>
        </template>
      </Tooltip>
      <FormButton class="w-full justify-center sm:w-auto" type="secondary" :disabled="isCancelDisabled || saveProcessing" @click="cancelSettings">
        Cancel
      </FormButton>
    </div>

    <alert-modal :message="'Uploaded image too large. Image must not exceed ' + maxPosterUploadMB + 'MB'"
      modal-id="embed-image-size-exceed" />
    <confirm-modal modal-id="confirm-copy-from-input-change" message="Are you sure you want to change this setting?"
      body="Changing it will remove the existing configuration and apply the new one" ok-text="Confirm"
      cancel-text="Cancel" @modal-confirm="onConfirmCopyFromInputChange" @modal-cancel="onCancelCopyFromInputChange"
      @modal-hidden="onCancelCopyFromInputChange" />
  </section>
</template>
